"use client";

import { useState, useEffect, useRef } from "react";
import { useMotionMode } from "@/context/MotionContext";
import { getScrimColor } from "@/utils/colors";
import { Footer } from "@/components/sections/Footer";
import { TopScrim } from "@/components/ui/TopScrim";
import { PulseDot } from "@/components/ui/PulseDot";

// =============================================================================
// RESUME OVERLAY
// =============================================================================
// Scrollable text resume with download option.
// Similar scroll pattern to ManifestoOverlay.

const RESUME_BLUE = "#5B8DEF";

// Resume data structure
const RESUME_DATA = {
  summary: "Product Designer with 10+ years shipping across 8+ platforms: web, mobile, living room (tvOS, consoles, streaming devices), voice, and spatial computing. Track record leading cross-platform strategic initiatives from concept through launch with measurable engagement outcomes.",
  experience: [
    {
      title: "Senior Product Designer",
      company: "Crunchyroll",
      period: "2022–Present",
      description: "Sole designer on the video player vertical, leading design across web, mobile, and 7+ living room platforms (Apple TV, Roku, Android TV, Fire TV, Smart TVs, Xbox, PlayStation, Nintendo Switch) for Crunchyroll's core streaming experience.",
      highlights: [
        { label: "Video Player Redesign", text: "Leading a company-wide strategic initiative to redesign the cross-platform video player across web, mobile, and living room, coordinating across multiple PMs and engineering teams to address the #1 user-requested feature." },
        { label: "Apple visionOS App", text: "Solo design lead for Crunchyroll's Day 1 visionOS app. Sustained 4.9/5.0 App Store rating, one of the highest-rated entertainment apps on Vision Pro." },
        { label: "End Slate Recommendations", text: "Shipped the post-episode engagement feature driving hundreds of millions of minutes watched." },
        { label: "Multiple Profiles", text: "Led LRX design from concept through launch across 7+ living room platforms. 20% feature adoption within the first month." },
      ],
    },
    {
      title: "Senior Product Designer",
      company: "Lovepop",
      period: "2021–2022",
      description: "Sole designer building design processes and team culture from scratch. Led retention-focused eCommerce initiatives and championed Lovepop's first design system.",
    },
    {
      title: "Senior Product Designer",
      company: "DraftKings",
      period: "2020–2021",
      description: "End-to-end platform design across consumer web, mobile, and internal tools serving the Promotional Marketing org. Contributed to Arena, the DraftKings design system.",
    },
    {
      title: "Senior Product Designer",
      company: "Drift",
      period: "2019–2020",
      description: "Owned design across multiple product squads. Shipped enterprise integrations (Zoom, Slack) and native iOS/Android features through direct customer partnership via early access programs.",
    },
    {
      title: "Senior User Experience Designer",
      company: "Panera Bread",
      period: "2016–2019",
      description: "Co-led omni-channel redesign across web, mobile, and in-store kiosk platforms. 3 years shaping the digital ordering experience at scale.",
    },
    {
      title: "User Experience Designer",
      company: "Education First",
      period: "2015–2016",
      description: "Designed responsive digital products for teachers across EF's global education platform.",
    },
  ],
  education: [
    { degree: "UXD Immersive", school: "General Assembly", year: "2015" },
    { degree: "B.A. Psychology", school: "Salem State University", year: "2014" },
  ],
};

interface ResumeOverlayProps {
  isActive: boolean;
  z1Height: number;
  z2Height: number;
  crtEnabled?: boolean;
  isLightMode?: boolean;
  onScrolledChange?: (scrolled: boolean) => void;
  onAtBottomChange?: (atBottom: boolean) => void;
}

export function ResumeOverlay({
  isActive,
  z1Height,
  z2Height,
  crtEnabled = true,
  isLightMode = false,
  onScrolledChange,
  onAtBottomChange,
}: ResumeOverlayProps) {
  const scrimColor = getScrimColor(isLightMode);
  const { canAnimate } = useMotionMode();
  const [showContent, setShowContent] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle visibility with fade timing
  useEffect(() => {
    if (isActive) {
      setIsVisible(true);
      const timer = setTimeout(() => setShowContent(true), 200);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsScrolled(false);
        setIsAtBottom(false);
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isActive]);

  // Scroll handling
  useEffect(() => {
    if (!isActive || !isVisible || !containerRef.current) return;
    const c = containerRef.current;

    const checkScrollState = () => {
      const scrolled = c.scrollTop > 20;
      setIsScrolled(scrolled);
      const atBottom = c.scrollTop + c.clientHeight >= c.scrollHeight - 50;
      setIsAtBottom(atBottom);
    };

    const onWheel = (e: WheelEvent) => {
      const rect = c.getBoundingClientRect();
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        e.preventDefault();
        c.scrollTop += e.deltaY;
        checkScrollState();
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const scrollAmount = 80;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        c.scrollTop += scrollAmount;
        checkScrollState();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        c.scrollTop -= scrollAmount;
        checkScrollState();
      }
    };

    const onScroll = () => {
      checkScrollState();
    };

    checkScrollState();

    window.addEventListener("wheel", onWheel, { passive: false });
    c.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("wheel", onWheel);
      c.removeEventListener("scroll", onScroll);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isActive, isVisible]);

  // Notify parent when scroll state changes
  useEffect(() => {
    onScrolledChange?.(isScrolled);
  }, [isScrolled, onScrolledChange]);

  // Notify parent when at bottom changes
  useEffect(() => {
    onAtBottomChange?.(isAtBottom);
  }, [isAtBottom, onAtBottomChange]);

  if (!isVisible) return null;

  const textShadow = crtEnabled ? "var(--crt-text-shadow)" : "none";

  return (
    <>
      <TopScrim isActive={isActive} isScrolled={isScrolled} z1Height={z1Height} isLightMode={isLightMode} />

      {/* Bottom scrim */}
      <div
        className="fixed left-0 right-0 z-[15] pointer-events-none"
        style={{
          bottom: 0,
          height: 200,
          background: `linear-gradient(to top, rgba(${scrimColor},1) 0%, rgba(${scrimColor},0.8) 30%, rgba(${scrimColor},0) 100%)`,
          opacity: isActive && !isAtBottom ? 1 : 0,
          transition: canAnimate ? `opacity ${isActive ? 400 : 150}ms ease-out` : "none",
        }}
      />

      {/* Scrollable container */}
      <div
        ref={containerRef}
        className="fixed inset-0 z-[12] overflow-y-auto overflow-x-hidden"
        style={{
          top: z1Height,
          maskImage: "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
        }}
        data-lenis-prevent
      >
        {/* Spacer - positions content at ~50vh like blog feed */}
        <div style={{ height: Math.max(16, Math.round(z2Height * 0.5 - z1Height * 0.5 + 10)) }} />

        <div
          className="max-w-4xl mx-auto px-3 sm:px-6 py-4 md:py-12"
          style={{
            opacity: showContent ? 1 : 0,
            transform: showContent ? "translateY(0)" : "translateY(20px)",
            transition: canAnimate
              ? "opacity 400ms ease-out, transform 400ms ease-out"
              : "none",
          }}
        >
          {/* Download button - centered */}
          <div className="flex justify-center mb-6 md:mb-8 md:mt-8">
            <a
              href="/resume.pdf"
              download="VincentNguResume.pdf"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs font-bold uppercase tracking-widest transition-all duration-200 hover:scale-105 pointer-events-auto"
              style={{
                background: `${RESUME_BLUE}20`,
                color: RESUME_BLUE,
                border: `1px solid ${RESUME_BLUE}40`,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Resume
            </a>
          </div>

          {/* Summary */}
          <p
            className="text-base leading-relaxed mb-12"
            style={{
              color: `rgba(var(--foreground-rgb), 0.7)`,
              textShadow,
            }}
          >
            {RESUME_DATA.summary}
          </p>

          {/* Experience Section */}
          <section className="mb-12">
            <h2
              className="text-xs font-mono font-bold uppercase tracking-[0.3em] mb-8"
              style={{
                color: RESUME_BLUE,
                textShadow,
              }}
            >
              Experience
            </h2>

            <div className="space-y-10">
              {RESUME_DATA.experience.map((job, i) => (
                <article key={i} className="relative">
                  {/* Timeline dot - hidden on mobile */}
                  <div
                    className="absolute -left-6 top-2.5 w-2 h-2 rounded-full hidden md:block"
                    style={{ backgroundColor: RESUME_BLUE, opacity: 0.5 }}
                  />

                  {/* Header */}
                  <div className="mb-2">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0">
                      <h3
                        className="text-lg font-headline font-bold"
                        style={{
                          color: `rgba(var(--foreground-rgb), 0.95)`,
                          textShadow,
                        }}
                      >
                        {job.title}
                      </h3>
                      <span
                        className="text-base hidden md:inline"
                        style={{ color: `rgba(var(--foreground-rgb), 0.5)` }}
                      >
                        •
                      </span>
                      <span
                        className="text-base font-medium hidden md:inline"
                        style={{ color: RESUME_BLUE }}
                      >
                        {job.company}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-x-2 mt-0.5">
                      <span
                        className="text-base font-medium md:hidden"
                        style={{ color: RESUME_BLUE }}
                      >
                        {job.company}
                      </span>
                      <span
                        className="text-sm font-mono flex items-center gap-1.5"
                        style={{ color: `rgba(var(--foreground-rgb), 0.4)` }}
                      >
                        {job.period}
                        {job.period.includes("Present") && (
                          <PulseDot size={5} color={RESUME_BLUE} glowColor={`${RESUME_BLUE}88`} />
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p
                    className="text-sm leading-relaxed mb-3"
                    style={{
                      color: `rgba(var(--foreground-rgb), 0.6)`,
                      textShadow,
                    }}
                  >
                    {job.description}
                  </p>

                  {/* Highlights */}
                  {job.highlights && (
                    <ul className="space-y-2 mt-4">
                      {job.highlights.map((h, j) => (
                        <li
                          key={j}
                          className="text-sm leading-relaxed pl-6 relative"
                          style={{ color: `rgba(var(--foreground-rgb), 0.55)` }}
                        >
                          <span
                            className="absolute left-0 top-0"
                            style={{ color: RESUME_BLUE }}
                          >
                            →
                          </span>
                          <span
                            className="font-semibold"
                            style={{ color: `rgba(var(--foreground-rgb), 0.8)` }}
                          >
                            {h.label}:
                          </span>{" "}
                          {h.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          </section>

          {/* Education Section */}
          <section>
            <h2
              className="text-xs font-mono font-bold uppercase tracking-[0.3em] mb-6"
              style={{
                color: RESUME_BLUE,
                textShadow,
              }}
            >
              Education
            </h2>

            <div className="space-y-4">
              {RESUME_DATA.education.map((edu, i) => (
                <div key={i}>
                  <div className="flex items-baseline gap-x-2">
                    <span
                      className="font-medium"
                      style={{
                        color: `rgba(var(--foreground-rgb), 0.8)`,
                        textShadow,
                      }}
                    >
                      {edu.degree}
                    </span>
                    <span
                      className="hidden md:inline"
                      style={{ color: `rgba(var(--foreground-rgb), 0.4)` }}
                    >
                      •
                    </span>
                    <span
                      className="hidden md:inline"
                      style={{ color: `rgba(var(--foreground-rgb), 0.6)` }}
                    >
                      {edu.school}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-x-2 mt-0.5">
                    <span
                      className="md:hidden"
                      style={{ color: `rgba(var(--foreground-rgb), 0.6)` }}
                    >
                      {edu.school}
                    </span>
                    <span
                      className="text-sm font-mono"
                      style={{ color: `rgba(var(--foreground-rgb), 0.4)` }}
                    >
                      {edu.year}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Bottom download button */}
          <div className="flex justify-center mt-12 mb-8">
            <a
              href="/resume.pdf"
              download="VincentNguResume.pdf"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs font-bold uppercase tracking-widest transition-all duration-200 hover:scale-105 pointer-events-auto"
              style={{
                background: `${RESUME_BLUE}20`,
                color: RESUME_BLUE,
                border: `1px solid ${RESUME_BLUE}40`,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Resume
            </a>
          </div>
        </div>

        {/* Cockpit footer - full-width to match About overlay */}
        <div
          className="px-4 sm:px-6 pb-12 pointer-events-auto"
          style={{
            opacity: showContent ? 1 : 0,
            transform: showContent ? "translateY(0)" : "translateY(20px)",
            transition: canAnimate
              ? "opacity 500ms ease-out 300ms, transform 600ms ease-out 300ms"
              : "none",
          }}
        >
          <Footer variant="cockpit" />
        </div>
      </div>
    </>
  );
}
